import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { MessageCircle, X, Send } from 'lucide-react';
import { Input } from '@/components/ui/input';

interface WhatsAppWidgetProps {
  phoneNumber?: string;
  welcomeMessage?: string;
  position?: 'bottom-right' | 'bottom-left';
}

export function WhatsAppWidget({ 
  phoneNumber = '5511999999999', // Substitua pelo número real
  welcomeMessage = 'Olá! Vi o Obrim e gostaria de saber mais.',
  position = 'bottom-right'
}: WhatsAppWidgetProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState(welcomeMessage);

  const handleSend = () => {
    const encodedMessage = encodeURIComponent(message);
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodedMessage}`;
    
    // Track no Pixel
    if (typeof window !== 'undefined' && (window as any).fbq) {
      (window as any).fbq('track', 'Contact', { method: 'whatsapp_widget' });
    }

    window.open(whatsappUrl, '_blank');
    setIsOpen(false);
  };

  const positionClasses = position === 'bottom-right' 
    ? 'right-4 md:right-6' 
    : 'left-4 md:left-6';

  return (
    <div className={`fixed bottom-4 md:bottom-6 ${positionClasses} z-50`}>
      {/* Chat Box */}
      {isOpen && (
        <div className="absolute bottom-16 right-0 w-[320px] bg-card border rounded-2xl shadow-xl overflow-hidden animate-in slide-in-from-bottom-5 duration-300">
          {/* Header */}
          <div className="bg-emerald-500 text-white p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div>
                  <p className="font-semibold">Suporte Obrim</p>
                  <p className="text-xs text-white/80">Responde em minutos</p>
                </div>
              </div>
              <button 
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white transition-colors"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Body */}
          <div className="p-4 bg-muted/30">
            <div className="bg-white dark:bg-card rounded-lg p-3 shadow-sm mb-4">
              <p className="text-sm text-foreground">
                Olá! 👋 Como posso te ajudar hoje?
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Normalmente respondo em poucos minutos
              </p>
            </div>

            {/* Input */}
            <div className="flex gap-2">
              <Input 
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Digite sua mensagem..."
                className="flex-1 h-10 text-sm"
                onKeyDown={(e) => e.key === 'Enter' && handleSend()}
              />
              <Button 
                size="sm" 
                className="h-10 px-3 bg-emerald-500 hover:bg-emerald-600"
                onClick={handleSend}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Floating Button */}
      <Button
        size="lg"
        className={`h-14 w-14 rounded-full shadow-lg ${
          isOpen 
            ? 'bg-muted text-foreground hover:bg-muted/80' 
            : 'bg-emerald-500 hover:bg-emerald-600 text-white'
        } transition-all duration-300`}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <X className="h-6 w-6" />
        ) : (
          <MessageCircle className="h-6 w-6" />
        )}
      </Button>

      {/* Notification Badge */}
      {!isOpen && (
        <span className="absolute top-0 right-0 flex h-4 w-4">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
          <span className="relative inline-flex rounded-full h-4 w-4 bg-emerald-500 border-2 border-white dark:border-background"></span>
        </span>
      )}
    </div>
  );
}
